// components/ModuleAssignmentModal.jsx
import { useState, useEffect } from "react";
import Toast from "../../shared/components/Toast.jsx";
import "../../styles/components/moduleAssignment.css";


// ── Checkbox ───────────────────────────────────────────────────────────────
const Checkbox = ({ state }) => (
  <span
    style={{
      width: 16,
      height: 16,
      borderRadius: 3,
      border: `1.5px solid ${state === "off" ? "#d1d5db" : "#6366f1"}`,
      background: state === "on" ? "#6366f1" : "#fff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      transition: "all 0.12s",
    }}
  >
    {state === "on" && (
      <span
        style={{
          display: "block",
          width: 8,
          height: 5,
          borderLeft: "1.5px solid #fff",
          borderBottom: "1.5px solid #fff",
          transform: "rotate(-45deg) translate(1px,-1px)",
        }}
      />
    )}
    {state === "half" && (
      <span
        style={{
          display: "block",
          width: 7,
          height: 1.5,
          background: "#6366f1",
          borderRadius: 1,
        }}
      />
    )}
  </span>
);

// ── Main Modal ─────────────────────────────────────────────────────────────
const ModuleAssignmentModal = ({ isOpen, onClose, org, onSave }) => {
  const [modules, setModules] = useState([]);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState(null);

  const showToast = (message, type = "success") => setToast({ message, type });

  // ── Fetch modules from API ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !org?.org_id) return;

    setLoading(true);
    setError(null);
    setModules([]);
    setChecked({});

    fetch(
      `https://brsrapi.digioxide.com/api/superadmin/organisations/${org.org_id}/modules-list`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("admin_token") ?? ""}`,
        },
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return res.json();
      })
      .then((json) => {
        const data        = json.data ?? [];
        const preselected = new Set(json.preselected_ids ?? []);

        const initial = {};
        data.forEach((mod) => {
          if (mod.submenus.length === 0) {
            // Leaf module — check its own id directly
            initial[mod.id] = preselected.has(mod.id);
          } else {
            // Parent with children — ALWAYS check each child individually.
            // Never bulk-select all children based on parent id being present,
            // because the API only adds the parent id as a convenience reference;
            // the source of truth for each child is whether that child's id
            // appears in preselected_ids.
            mod.submenus.forEach((sub) => {
              initial[sub.id] = preselected.has(sub.id);
            });
          }
        });

        setModules(data);
        setChecked(initial);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [isOpen, org?.org_id]);

  if (!isOpen) return null;

  // ── Helpers ────────────────────────────────────────────────────────────────
  const isLeaf = (mod) => mod.submenus.length === 0;

  const parentState = (mod) => {
    if (isLeaf(mod)) return checked[mod.id] ? "on" : "off";
    const total = mod.submenus.length;
    const on    = mod.submenus.filter((s) => checked[s.id]).length;
    if (on === 0)     return "off";
    if (on === total) return "on";
    return "half";
  };

  // ── Toggle handlers ────────────────────────────────────────────────────────
  const toggleLeaf   = (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleChild  = (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleParent = (mod) => {
    const allOn = parentState(mod) === "on";
    setChecked((prev) => {
      const next = { ...prev };
      mod.submenus.forEach((s) => { next[s.id] = !allOn; });
      return next;
    });
  };

  const selectAll = () => {
    const next = {};
    modules.forEach((mod) => {
      if (isLeaf(mod)) {
        next[mod.id] = true;
      } else {
        mod.submenus.forEach((sub) => { next[sub.id] = true; });
      }
    });
    setChecked(next);
  };

  const clearAll = () => setChecked({});

  // ── Totals ─────────────────────────────────────────────────────────────────
  const totalAmount = modules.reduce((sum, mod) => {
    if (isLeaf(mod)) return sum + (checked[mod.id] ? parseFloat(mod.cost) : 0);
    return (
      sum +
      mod.submenus.reduce(
        (s, sub) => s + (checked[sub.id] ? parseFloat(sub.cost) : 0),
        0
      )
    );
  }, 0);

  const selectedCount = Object.values(checked).filter(Boolean).length;

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);

    const checkedIds   = [];
    const uncheckedIds = [];

    modules.forEach((mod) => {
      if (isLeaf(mod)) {
        if (checked[mod.id]) checkedIds.push(mod.id);
        else uncheckedIds.push(mod.id);
      } else {
        let anyChildChecked = false;
        mod.submenus.forEach((sub) => {
          if (checked[sub.id]) {
            checkedIds.push(sub.id);
            anyChildChecked = true;
          } else {
            uncheckedIds.push(sub.id);
          }
        });
        if (anyChildChecked) checkedIds.push(mod.id);
        else uncheckedIds.push(mod.id);
      }
    });

    try {
      const response = await fetch(
        `https://brsrapi.digioxide.com/api/superadmin/organisations/${org.org_id}/assign-modules`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("admin_token") ?? ""}`,
          },
          body: JSON.stringify({
            checked_ids:   checkedIds,
            unchecked_ids: uncheckedIds,
          }),
        }
      );

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json?.message ?? `Request failed (${response.status})`);
      }

      await onSave?.({ checkedIds, uncheckedIds });

      showToast("Modules assigned successfully!", "success");
      setTimeout(() => {
        setToast(null);
        onClose();
      }, 1500);

    } catch (err) {
      showToast(err.message ?? "Failed to assign modules. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        className="mam-overlay"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="mam-dialog">

          {/* Header */}
          <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid #f0f0f4", display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 2 }}>
                Module assignment
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {org?.organization_name ?? "Organisation"} — select modules to assign
              </div>
            </div>
            <button className="mam-close" onClick={onClose}>×</button>
          </div>

          {/* Toolbar */}
          <div style={{ padding: "8px 22px", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid #f0f0f4", flexShrink: 0, background: "#fafafa" }}>
            <button className="mam-tbtn" onClick={selectAll} disabled={loading}>Select all</button>
            <button className="mam-tbtn" onClick={clearAll}  disabled={loading}>Clear all</button>
            <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>
              <strong style={{ color: "#111827" }}>{selectedCount}</strong> selected
            </span>
          </div>

          {/* Body */}
          <div className="mam-body">

            {loading && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 0", gap: 10 }}>
                <div className="mam-spinner" />
                <div style={{ fontSize: 13, color: "#9ca3af" }}>Loading modules…</div>
              </div>
            )}

            {!loading && error && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 0", gap: 8 }}>
                <div style={{ fontSize: 13, color: "#ef4444" }}>{error}</div>
              </div>
            )}

            {!loading && !error && modules.map((mod) => {
              const state    = parentState(mod);
              const isActive = state !== "off";

              if (isLeaf(mod)) {
                return (
                  <div key={mod.id} className={`mam-card ${isActive ? "active" : ""}`}>
                    <div className="mam-prow" onClick={() => toggleLeaf(mod.id)}>
                      <Checkbox state={state} />
                      <span className="mam-plbl">{mod.name}</span>
                      <span className="mam-price">
                        ₹{parseFloat(mod.cost).toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                );
              }

              const groupTotal = mod.submenus.reduce(
                (s, sub) => s + (checked[sub.id] ? parseFloat(sub.cost) : 0),
                0
              );

              return (
                <div key={mod.id} className={`mam-card ${isActive ? "active" : ""}`}>
                  <div className="mam-prow" onClick={() => toggleParent(mod)}>
                    <Checkbox state={state} />
                    <span className="mam-plbl">{mod.name}</span>
                    {groupTotal > 0 && (
                      <span className="mam-gprice">
                        ₹{Math.round(groupTotal).toLocaleString("en-IN")}
                      </span>
                    )}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>

                  {mod.submenus.map((sub) => (
                    <div key={sub.id} className="mam-crow" onClick={() => toggleChild(sub.id)}>
                      <Checkbox state={checked[sub.id] ? "on" : "off"} />
                      <span className="mam-clbl">{sub.name}</span>
                      <span className="mam-price">
                        ₹{parseFloat(sub.cost).toLocaleString("en-IN")}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ borderTop: "1px solid #f0f0f4", padding: "14px 22px", display: "flex", alignItems: "center", flexShrink: 0, background: "#fafafa" }}>
            <div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 1 }}>Total amount</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: -0.5 }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: "#9ca3af", marginRight: 1 }}>₹</span>
                {Math.round(totalAmount).toLocaleString("en-IN")}
              </div>
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button className="mam-cancel" onClick={onClose}>Cancel</button>
              <button
                className="mam-save"
                onClick={handleSave}
                disabled={saving || selectedCount === 0 || loading}
              >
                {saving ? "Saving…" : "Assign modules"}
              </button>
            </div>
          </div>

        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default ModuleAssignmentModal;
